import React from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDocumentTypesContext } from '../context/DocumentTypesContext';
import { useShortcut } from '../context/ShortcutContext';
import { useToast } from '../context/ToastContext';
import { StockTransferDraftState } from '../types/stockTransferDraft';

interface SampleTransferRow {
  rowId?: string;
  uzurkuvanje?: string;
  subprocessId?: string;
  itemId: string;
  ident: string;
  name: string;
  serialNumber?: string;
  expiryDate?: string;
}

interface InterProductReceipt {
  subprocessId: string;
  warehouse?: string;
  rows?: Array<{ rowKey?: string; serial?: string }>;
}

interface UseRNMaterialPriemSampleTransferArgs {
  rows: SampleTransferRow[];
  rnWorkOrderId?: string;
  rnWorkOrderNumber?: string;
  interProductReceipts?: InterProductReceipt[];
}

export const useRNMaterialPriemSampleTransfer = ({
  rows,
  rnWorkOrderId,
  rnWorkOrderNumber,
  interProductReceipts = [],
}: UseRNMaterialPriemSampleTransferArgs) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { allDocumentTypes } = useDocumentTypesContext();
  const { addShortcut, hasShortcut } = useShortcut();
  const { showToast } = useToast();
  const [isPending, setIsPending] = React.useState(false);
  const pendingRef = React.useRef(false);

  const sampleTransferDocument = React.useMemo(
    () => allDocumentTypes.find((documentType) =>
      documentType.workOrderRole === 'sample-transfer' && documentType.code.trim()
    ),
    [allDocumentTypes]
  );
  const disabledReason = !sampleTransferDocument ? 'MissingDocumentType'
    : !rnWorkOrderId ? 'Unsaved'
      : isPending ? 'Pending' : 'Tooltip';

  const handleSampledClick = React.useCallback(async () => {
    if (pendingRef.current || disabledReason !== 'Tooltip' || !sampleTransferDocument) return;

    const sampledRows = rows.filter((row) => (row.uzurkuvanje || '').trim() !== '');
    if (sampledRows.length === 0) {
      showToast(t('rnUzurkuvanjeRequests.noSampledRows'), 'error');
      return;
    }

    pendingRef.current = true;
    setIsPending(true);
    try {
      const sourceStage = sampledRows[0]?.subprocessId || '';
      const sourceWarehouseIds = sampledRows.map((sampledRow) => {
        const stageReceipts = interProductReceipts.filter((receipt) => receipt.subprocessId === sampledRow.subprocessId);
        const matchingReceipt = stageReceipts.find((receipt) =>
          sampledRow.rowId && receipt.rows?.some((row) => row.rowKey === sampledRow.rowId)
        ) || stageReceipts.find((receipt) =>
          sampledRow.serialNumber && receipt.rows?.some((row) => row.serial === sampledRow.serialNumber)
        );
        return matchingReceipt?.warehouse || (stageReceipts.length === 1 ? stageReceipts[0]?.warehouse || '' : '');
      });
      const sourceWarehouseId = sourceWarehouseIds[0] || '';
      if (sourceWarehouseIds.some((warehouseId) => !warehouseId)) {
        showToast(t('rnUzurkuvanjeRequests.sourceWarehouseMissing'), 'error');
        return;
      }
      if (sourceWarehouseIds.some((warehouseId) => warehouseId !== sourceWarehouseId)) {
        showToast(t('stockOverview.transferSameWarehouseRequired'), 'error');
        return;
      }
      if (sampledRows.some((row) => row.subprocessId !== sourceStage)) {
        showToast(t('stockOverview.transferSameWarehouseRequired'), 'error');
        return;
      }
      const documentCode = sampleTransferDocument.code;
      const path = `/material/transfer/${documentCode}`;

      if (rnWorkOrderId && sourceStage) {
        try {
          const token = localStorage.getItem('token');
          const apiUrl = process.env.REACT_APP_API_URL ?? '';
          const res = await axios.get(`${apiUrl}/api/documents-transfer/by-type/${documentCode}/all`, {
            headers: { 'x-auth-token': token ?? '' },
            params: {
              sourceWorkOrderId: rnWorkOrderId,
              sourceStage,
              page: 1,
              limit: 1,
            },
          });
          const existing = res.data?.docs?.[0];
          if (existing) {
            showToast(t('rnUzurkuvanjeRequests.alreadySampled', { number: existing.number || '' }), 'error');
            return;
          }
        } catch {
          // The server still enforces the one-document-per-stage rule when saving.
        }
      }

      if (!hasShortcut(path)) {
        addShortcut({
          documentCode,
          documentName: sampleTransferDocument.name || t('sidebar.transferDocuments'),
          category: 'material',
          subCategory: 'transfer',
          path,
        });
      }

      const stockTransferDraft: StockTransferDraftState = {
        requestId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        source: 'rnUzorkuvanje',
        sourceWarehouseId,
        workOrderId: rnWorkOrderId,
        workOrderNumber: rnWorkOrderNumber,
        sourceStage,
        rows: sampledRows.map((row) => ({
          itemId: row.itemId,
          ident: row.ident,
          name: row.name,
          serial: row.serialNumber || '',
          deadline: row.expiryDate || '',
          quantity: String(row.uzurkuvanje).trim(),
        })),
      };
      navigate(path, { state: { stockTransferDraft } });
    } finally {
      pendingRef.current = false;
      setIsPending(false);
    }
  }, [addShortcut, disabledReason, hasShortcut, interProductReceipts, navigate, rnWorkOrderId, rnWorkOrderNumber, rows, sampleTransferDocument, showToast, t]);

  return {
    sampleTransferDisabled: disabledReason !== 'Tooltip',
    sampleTransferTitle: disabledReason === 'MissingDocumentType'
      ? t('rnUzurkuvanjeRequests.sampleTransferTypeMissing')
      : disabledReason === 'Unsaved'
        ? t('rnUzurkuvanjeRequests.saveFirst')
        : undefined,
    handleSampledClick,
  };
};
